import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import { Alert } from '@material-ui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Form from '../../form/components/Form';
import DateInput from '../../form/components/DateInput';
import SubmitButton from '../../form/components/SubmitButton';
import { useInitialValues, useResourceSubmit } from '../../form/hooks';
import { CARE_NEED_RESOURCE, NURSE_DEPLOYMENT_RESOURCE, NURSE_SUGGESTION_RESOURCE } from '../../api/resources';
import { IdPropType, RefPropType } from '../../../proptypes/basic';
import { ContractPropType } from '../../../proptypes/contracts';
import { acceptNurseSuggestionSchema } from '../schema';
import { useNurseSuggestion, useNurseSuggestions, selectAllNurseSuggestions } from '../nurseSuggestionsSlice';
import {
    updateNurseRequest,
    useNurseRequest,
    useNurseRequestsByCareNeedId,
} from '../../nurseRequests/nurseRequestsSlice';
import { useCareNeed } from '../../careNeeds/careNeedsSlice';
import ContractSelect from '../../contracts/components/ContractSelect';
import LoadingBox from '../../loading/components/LoadingBox';
import { useApi } from '../../api/components/ApiProvider';
import { EMAIL_RESOURCE } from '../../api/resources';
import { useResourceDialog } from '../../dialogs/hooks';
import { selectAllAgencies } from '../../agencies/agenciesSlice';
import { selectAllEmployees } from '../../employees/employeesSlice';
import { useEmailInboxAddressBySlug } from '../../emailInboxes/emailInboxesSlice';
import { useGetUserEmplyeeId } from '../../abilities/hooks';
import {
    useNurseDeploymentsByCustomerContractId
} from '../../nurseDeployments/nurseDeploymentsSlice';
import { useContractsByCustomerId } from '../../contracts/contractsSlice';
import { AGENCY_CONTRACT } from '../../contracts/contractTypes';
import { formatDate, DATETIME_DISPLAY_FORMAT } from '../../date/utils';
import { useCustomer } from '../../customers/customersSlice';
import { selectLocationById } from '../../locations/locationsSlice';
import { DEFAULT_SYSTEM_FEE, NO_SYSTEM_FEE_LOCATION_IDS } from '../../nurseDeployments/constants';
import { EMAIL_INBOX_SLUG_KUNDENBETREUUNG } from '../../emailInboxes/constants';
import { useNurse } from '../../nurses/nursesSlice';

// DEFAULT_SYSTEM_FEE and NO_SYSTEM_FEE_LOCATION_IDS mirror
// NurseDeploymentForm's updateSystemFee(). That logic only ever runs when a
// deployment is created through NurseDeploymentForm itself; this form
// creates deployments directly (see handleSubmit below) and needs the same
// default applied by hand, or accepted suggestions silently end up with no
// system fee at all.

// Pre-selects the contract for the agency that suggested this nurse, so staff
// don't have to manually pick it whenever there's an obvious match - it only
// ever fills in a still-empty field (never overrides a value already chosen
// or loaded), and only once contracts have actually loaded. It has to live
// inside <Form> (not the surrounding component) to reach Formik's context.
const DefaultAgencyContractSelector = ({ contracts, agencyId }) => {
    const { values, setFieldValue } = useFormikContext();
    const applied = useRef(false);

    useEffect(() => {
        if (applied.current || values.customerContractId || !agencyId || contracts.length === 0) {
            return;
        }
        const eligibleContracts = contracts.filter(
            (contract) =>
                !contract.parentContractId &&
                contract.type === AGENCY_CONTRACT &&
                contract.agencyId === agencyId
        );
        // prefer a still-running contract over an already-ended one
        const activeContract = eligibleContracts.find(
            (contract) => !contract.endAt || new Date(contract.endAt) >= new Date()
        );
        const match = activeContract || eligibleContracts[0];
        if (match) {
            applied.current = true;
            setFieldValue('customerContractId', match.id);
        }
    }, [contracts, agencyId, values.customerContractId, setFieldValue]);

    return null;
};

DefaultAgencyContractSelector.propTypes = {
    contracts: PropTypes.arrayOf(ContractPropType).isRequired,
    agencyId: IdPropType,
};

DefaultAgencyContractSelector.defaultProps = {
    agencyId: null,
};

// Renders "<nurse name> (<agency name>)" for one already-confirmed sibling
// suggestion, inline in a sentence. Deliberately not NurseName - that goes
// through LoadingBox, which wraps its content in a block-level <div> and
// would force a line break mid-sentence here. Returning a plain string keeps
// this a text node with no wrapping element at all.
const ConfirmedSuggestionSummary = ({ suggestion, nurseRequests, agencies }) => {
    const [nurse] = useNurse(suggestion.nurseId);
    const suggestionNurseRequest = nurseRequests.find(
        (request) => request.id === suggestion.nurseRequestId
    );
    const agency = agencies.find((a) => a.id === suggestionNurseRequest?.agencyId);
    const nurseName = nurse ? `${nurse.firstname} ${nurse.lastname}` : '…';
    return agency ? `${nurseName} (${agency.name})` : nurseName;
};

ConfirmedSuggestionSummary.propTypes = {
    suggestion: PropTypes.shape({
        nurseId: IdPropType,
        nurseRequestId: IdPropType,
    }).isRequired,
    nurseRequests: PropTypes.arrayOf(PropTypes.shape({ id: IdPropType, agencyId: IdPropType }))
        .isRequired,
    agencies: PropTypes.arrayOf(PropTypes.shape({ id: IdPropType, name: PropTypes.string }))
        .isRequired,
};

const AcceptNurseSuggestionForm = ({ nurseSuggestionId, onClose, submitRef }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [
        nurseSuggestion,
        { loading: nurseSuggestionLoading, initialized: nurseSuggestionInitialized },
    ] = useNurseSuggestion(nurseSuggestionId);
    const [nurseRequest, { loading: nurseRequestLoading, initialized: nurseRequestInitialized }] =
        useNurseRequest(nurseSuggestion && nurseSuggestion.nurseRequestId);
    const [
        nurseRequests,
        { loading: nurseRequestsLoading, initialized: nurseRequestsInitialized },
    ] = useNurseRequestsByCareNeedId(nurseRequest && nurseRequest.careNeedIds[0]);
    const [careNeed, { loading: careNeedLoading, initialized: careNeedInitialized }] =
        useCareNeed(nurseRequest && nurseRequest.careNeedIds[0]);

    const openNurseRequests = nurseRequests.filter((request, index, self) => {
        return request.closedAt === null || request.closedAt === '';
    });

    // Before this NurseSuggestion can be accepted, every other NurseSuggestion we
    // have for the same customer must already be rejected (rejectedAt, rejectedBy
    // and internalRejectionReason all filled in). The submit button has to stay
    // disabled (and the form show a loading state) until this data has arrived,
    // otherwise it would look accept-able before we actually know that.
    const {
        loading: allNurseSuggestionsLoading,
        initialized: allNurseSuggestionsInitialized,
    } = useNurseSuggestions();
    const allNurseSuggestions = useSelector(selectAllNurseSuggestions);
    const otherNurseSuggestionsForCustomer = allNurseSuggestions.filter(
        (suggestion) =>
            // nurseSuggestionId is a prop and may be a string, while suggestion.id
            // comes from the normalized store and is a number - compare as strings
            // so the current suggestion doesn't get mistaken for "another" one.
            String(suggestion.id) !== String(nurseSuggestionId) &&
            // only suggestions tied to a still-open request are relevant here - a
            // suggestion left over from an already-closed request will never get
            // formally rejected and shouldn't block accepting this one.
            openNurseRequests.some((request) => request.id === suggestion.nurseRequestId)
    );
    const unrejectedOtherNurseSuggestions = otherNurseSuggestionsForCustomer.filter(
        (suggestion) =>
            // a suggestion that's already confirmed is a kept decision, not a
            // pending one - it doesn't need to be rejected too. This matters
            // once nursesNeeded > 1: the same request can carry several
            // already-confirmed candidates (e.g. one agency supplying both
            // nurses a care need needs), and none of those should block
            // accepting another one.
            !suggestion.confirmedAt &&
            (!suggestion.rejectedAt || !suggestion.rejectedBy || !suggestion.internalRejectionReason)
    );
    // Split by where they come from, so the message below can say whether
    // it's this same (possibly last, only open) request that has another
    // undecided candidate (e.g. several candidates the same agency
    // proposed), or a genuinely different agency's request - both count
    // equally toward the isLastNeededConfirmation gate below (a same-request
    // sibling left stranded, not-confirmed, is just as unreachable once the
    // request closes as one from another agency), this split only changes
    // how the warning explains itself.
    const unrejectedSameRequestSuggestions = unrejectedOtherNurseSuggestions.filter(
        (suggestion) => suggestion.nurseRequestId === nurseRequest?.id
    );
    const unrejectedOtherRequestSuggestions = unrejectedOtherNurseSuggestions.filter(
        (suggestion) => suggestion.nurseRequestId !== nurseRequest?.id
    );

    // How many nurses this care need still requires (defaults to 1 - the
    // long-standing implicit assumption before nursesNeeded existed).
    const nursesNeeded = careNeed?.nursesNeeded || 1;

    // Suggestions already confirmed for this care need, not counting the
    // suggestion being accepted right now (it isn't saved yet at this point).
    // Kept as the full list, not just a count, so the UI can show staff
    // *which* nurses those confirmations belong to - a bare "2 already
    // confirmed" with no way to see who is exactly what confused staff
    // reading it (there's no other screen where those stay visible once
    // their nurse request closes, see the comment on isLastNeededConfirmation
    // below).
    //
    // Also excludes confirmations whose own care period has already ended
    // (careTill in the past) - a care need can need replacement staff once
    // an earlier confirmed nurse's assignment is over, and that earlier,
    // finished placement no longer occupies a nursesNeeded slot. Without
    // this, a long-since-completed confirmation from months ago would keep
    // counting forever and show up here as "already confirmed" even though
    // it has nothing to do with the request being decided right now.
    const today = new Date();
    const confirmedNurseRequestIds = nurseRequests.map((request) => request.id);
    const otherConfirmedSuggestions = allNurseSuggestions.filter(
        (suggestion) =>
            String(suggestion.id) !== String(nurseSuggestionId) &&
            suggestion.confirmedAt &&
            !suggestion.rejectedAt &&
            (!suggestion.careTill || new Date(suggestion.careTill) >= today) &&
            confirmedNurseRequestIds.includes(suggestion.nurseRequestId)
    );
    const otherConfirmedSuggestionsCount = otherConfirmedSuggestions.length;

    // This suggestion always counts toward nursesNeeded, whether it's the one
    // about to be confirmed (isLastNeededConfirmation preview below) or - if
    // nurseSuggestion?.confirmedAt is already set - one that was confirmed
    // earlier. Comparing this to nursesNeeded can come out *above* 100% (e.g.
    // 3 confirmed but only 2 needed): that's not this form double-counting
    // anything, it's a real, legitimate history - e.g. nursesNeeded was
    // lowered to 2 after 3 suggestions were already confirmed against it
    // being 3. isOverbooked below only flags that state for staff to look
    // at, it doesn't try to resolve it.
    const totalConfirmedIncludingThis = otherConfirmedSuggestionsCount + 1;
    const isOverbooked = totalConfirmedIncludingThis > nursesNeeded;

    // Accepting this form always confirms the suggestion (confirmedAt is a
    // required field of acceptNurseSuggestionSchema), so it always fills one
    // more slot. Other still-pending suggestions only turn into something
    // that *must* be rejected first once this confirmation is the one that
    // reaches (or exceeds) nursesNeeded - while slots remain open below that,
    // e.g. confirming 2 of 3 presented candidates when only 2 are needed,
    // leaving the rest undecided for now is fine.
    //
    // This has to line up with the "enoughNursesConfirmed" check in
    // handleSubmit below (same inputs, same threshold): that's the one
    // moment where every still-open nurse request for this care need gets
    // closed at once, which makes the card disappear from "BK-Vorgestellt"
    // (see getDecisions() on the backend) and, with it, every row's
    // Ablehnungsdatum/abgelehnt durch/Ablehnungsgrund fields become
    // unreachable. Forcing the reject-others gate on exactly this
    // confirmation - and not before - is what guarantees no suggestion is
    // ever left stranded, half-decided, once that happens.
    const isLastNeededConfirmation = totalConfirmedIncludingThis >= nursesNeeded;

    // canAcceptSuggestion can be false for two unrelated reasons - either the data
    // that decides this hasn't finished loading yet, or it has and there really are
    // other, still unrejected suggestions. acceptBlockedReason spells out which one
    // it is so the message shown to the user (and the disabled submit button) makes
    // sense instead of just silently blocking.
    let acceptBlockedReason = null;
    if (!allNurseSuggestionsInitialized) {
        acceptBlockedReason =
            'Andere Personalvorschläge für diesen Kunden werden noch geladen …';
    } else if (nurseSuggestion?.confirmedAt) {
        // This suggestion was already accepted - resubmitting would create a
        // second nurse_deployment for it (handleDeploymentSubmit always
        // creates, it never updates) and, once enough nurses are confirmed,
        // re-send the "no longer needed" email to the other agencies again.
        acceptBlockedReason = `Dieser Personalvorschlag wurde bereits am ${formatDate(
            nurseSuggestion.confirmedAt,
            DATETIME_DISPLAY_FORMAT
        )} bestätigt und kann nicht erneut angenommen werden.`;
    } else if (isLastNeededConfirmation && unrejectedOtherNurseSuggestions.length > 0) {
        const sameCount = unrejectedSameRequestSuggestions.length;
        const otherCount = unrejectedOtherRequestSuggestions.length;
        const parts = [];
        if (sameCount > 0) {
            const suggestionWord = sameCount === 1 ? 'Personalvorschlag' : 'Personalvorschläge';
            parts.push(
                `${sameCount} nicht abgelehnte${sameCount === 1 ? 'r' : ''} ${suggestionWord} dieser Anfrage`
            );
        }
        if (otherCount > 0) {
            const suggestionWord = otherCount === 1 ? 'Personalvorschlag' : 'Personalvorschläge';
            parts.push(
                `${otherCount} nicht abgelehnte${
                    otherCount === 1 ? 'r' : ''
                } ${suggestionWord} anderer offener BK-Anfragen für diesen Kunden`
            );
        }
        const nursesNeededText =
            nursesNeeded === 1 ? 'wird 1 BK benötigt' : `werden ${nursesNeeded} BKs benötigt`;
        acceptBlockedReason = `Es ${nursesNeededText}! Momentan liegen noch ${parts.join(' sowie ')} vor. Bitte diese zuerst ablehnen (Ablehnungsdatum, abgelehnt durch und Ablehnungsgrund ausfüllen), bevor dieser Vorschlag angenommen werden kann.`;
    }
    const canAcceptSuggestion = acceptBlockedReason === null;

    const [activeCustomerContractId, setActiveCustomerContractId] = useState(undefined);

    const agencies = useSelector(selectAllAgencies);
    const employees = useSelector(selectAllEmployees);
    const employeeId = useGetUserEmplyeeId();
    const userEmployee = employees.find((e) => e.id === employeeId);
    const userStandardEmailInbox = userEmployee ? userEmployee.emailInboxes.find((inbox) => inbox.isDefault) : null;
    const userStandardEmailAddress = userStandardEmailInbox ? userStandardEmailInbox.name : null;
    const kundenbetreuungEmailAddress = useEmailInboxAddressBySlug(EMAIL_INBOX_SLUG_KUNDENBETREUUNG);
    //console.log("userEmployee =>", userEmployee, "userStandardEmailAddress =>", userStandardEmailAddress);

    // Same inputs NurseDeploymentForm's updateSystemFee() uses: the logged
    // in employee's own location decides whether a system fee applies at
    // all, while the customer's location (which can be a different
    // location than the employee's) is checked against the exclusion list.
    const [customer, { loading: customerLoading, initialized: customerInitialized }] =
        useCustomer(nurseRequest?.customerId);
    const employeeLocation = useSelector((state) => selectLocationById(state, userEmployee?.locationId));
    const isFranchiseExpert = employeeLocation?.franchiseType === 'Expert';
    const systemFee =
        isFranchiseExpert && !NO_SYSTEM_FEE_LOCATION_IDS.includes(customer?.locationId)
            ? DEFAULT_SYSTEM_FEE
            : 0.0;

    const loading =
        nurseSuggestionLoading ||
        nurseRequestLoading ||
        nurseRequestsLoading ||
        careNeedLoading ||
        customerLoading ||
        allNurseSuggestionsLoading;
    const initialized =
        nurseSuggestionInitialized &&
        nurseRequestInitialized &&
        customerInitialized &&
        nurseRequestsInitialized &&
        careNeedInitialized &&
        allNurseSuggestionsInitialized;

    const handleEditCareNeed = useResourceDialog(CARE_NEED_RESOURCE, {
        customerId: nurseRequest?.customerId,
    });

    const [contracts] = useContractsByCustomerId(nurseRequest?.customerId);

    const [allNurseDeploymentsByCustomerContractId] = useNurseDeploymentsByCustomerContractId(activeCustomerContractId);
    const acceptNurseSuggSchema = acceptNurseSuggestionSchema(allNurseDeploymentsByCustomerContractId);

    const initialValues = useInitialValues(nurseSuggestion, {
        confirmedAt: null,
        agencyConfirmedAt: null,
        customerContractId: '',
        careFrom: nurseSuggestion?.careFrom || null,
        careTill: nurseSuggestion?.careTill || null,
    });

    const api = useApi();
    const handleDeploymentSubmit = useResourceSubmit(null, NURSE_DEPLOYMENT_RESOURCE);
    const handleSuggestionSubmit = useResourceSubmit(nurseSuggestionId, NURSE_SUGGESTION_RESOURCE);

    const updateContractInformation = useCallback((customerContract, setfieldvalue) => {
        if (customerContract === undefined || customerContract === null)
            return;
        setActiveCustomerContractId(customerContract.id);
        setfieldvalue('customerContractEndAt', customerContract.endAt);
    }, []);


    const handleOnChange = (newvalues, initvalues, setfieldvalue) => {
        //console.log('newvalues=>', newvalues.type, 'initvalues=>', initialValues.type);
        if (newvalues.customerContractId !== null && newvalues.customerContractId !== '') {
            const customerContract = contracts.find(
                (contract) => contract.id === newvalues.customerContractId
            );
            updateContractInformation(customerContract, setfieldvalue);
        }
    }

    const handleSubmit = useCallback(
        async (values) => {
            // safety net in case the (disabled) submit button gets bypassed somehow
            if (!canAcceptSuggestion) {
                throw new Error(acceptBlockedReason);
            }

            // Only auto-cancel the remaining agencies once this care need is
            // actually fully staffed - accepting the Nth suggestion doesn't
            // mean we're done if nursesNeeded > 1 and fewer than that are
            // confirmed so far. Staff can still cancel everything manually
            // via "Alle BK-Anfragen absagen" regardless of this count - that
            // button is an explicit human decision and isn't gated here.
            const willBeConfirmed = Boolean(values.confirmedAt);
            const totalConfirmedCount = otherConfirmedSuggestionsCount + (willBeConfirmed ? 1 : 0);
            const enoughNursesConfirmed = totalConfirmedCount >= nursesNeeded;

            if (enoughNursesConfirmed) {
                //send a request cancelation message to all other agencies / filter out multiple agency id first
                // Sent one at a time (not Promise.all) - these all share the same
                // authenticated session, and the API serializes concurrent requests
                // from one session, so firing them all in parallel just makes them
                // queue up server-side and risks a gateway timeout on whichever one
                // is still waiting when the proxy gives up.
                const otherAgencyRequests = openNurseRequests
                    .filter((request, index, self) => {
                        return index === self.findIndex((r) => r.agencyId === request.agencyId );
                    })
                    .filter((request, index, self) => {
                        return request.agencyId !== nurseRequest.agencyId;
                    });

                for (const request of otherAgencyRequests) {
                    const agency = agencies.find((agency) => agency.id === request.agencyId);
                    if (agency == null) {
                        console.error('Invalid agencyId', request.agencyId);
                        continue;
                    }

                    // include the rejection details (date, by whom, reason) of the
                    // suggestion(s) we had running for this agency's request, so the
                    // agency understands why we no longer need a proposal from them.
                    const rejectionDetails = otherNurseSuggestionsForCustomer
                        .filter((suggestion) => suggestion.nurseRequestId === request.id)
                        .map((suggestion) => {
                            const rejectedAtLabel = formatDate(
                                suggestion.rejectedAt,
                                DATETIME_DISPLAY_FORMAT
                            );
                            const rejectedByLabel = suggestion.rejectedBy
                                ? t(`rejectedBy.${suggestion.rejectedBy}`)
                                : '-';
                            const reasonLabel = suggestion.internalRejectionReason
                                ? t(`rejectionReason.${suggestion.internalRejectionReason}`)
                                : '-';
                            return `Abgelehnt am: ${rejectedAtLabel}<br />Abgelehnt von: ${rejectedByLabel}<br />Ablehnungsgrund: ${reasonLabel}`;
                        })
                        .join('<br /><br />');

                    const email_text = `Liebe Agentur ${agency.name}, <br /> <br />\
unser Kunde hat sich soeben für einen Personalvorschlag entschieden. Für diesen Kunden benötigen wir von Euch daher keinen Vorschlag mehr. <br /> <br />\
${rejectionDetails ? `${rejectionDetails}<br /> <br />` : ''}\
Sollte uns Eurerseits bereits ein Personalvorschlag vorliegen, gebt uns bitte Bescheid, ob die Betreuungskraft auch für einen anderen Kunden in Frage kommt, dann stellen wir sie dort sehr gerne vor. <br />\
Wir danken Euch für Eure Unterstützung. <br /> <br />\
*** Dies ist eine automatisch generierte E-Mail und dient zur schnelleren Kommunikation. ***<br /> <br />`;

                    let agency_email = null;
                    const defaultAgencyEmployee = employees.find((e) => {
                        return (
                            String(e.agencyId) === String(request.agencyId) &&
                            e.isPrimaryContact
                        );
                    });

                    if (defaultAgencyEmployee) {
                        agency_email = defaultAgencyEmployee.email;
                    }

                    if (agency_email === null) {
                        console.error(
                            'no legal agency email address found for agencyId',
                            request.agencyId
                        );
                        continue;
                    }

                    const fromAddress = userStandardEmailAddress ?? kundenbetreuungEmailAddress;
                    if (fromAddress == null) {
                        console.error(
                            "no from email address available for cancelation email (neither the user's standard inbox nor the",
                            EMAIL_INBOX_SLUG_KUNDENBETREUUNG,
                            'inbox could be resolved) for agencyId',
                            request.agencyId
                        );
                        continue;
                    }

                    const preppedValues = {
                        from: fromAddress,
                        subject: `#${request.customerId} Vielen Dank für Eure Unterstützung`,
                        text: email_text,
                        receiver: [agency_email],
                        attachments: [],
                        customer_id: request.customerId,
                        is_premium: false,
                    };
                    console.log("send request cancelation email => request.id / preppedValues =>",request.id, preppedValues);
                    // await so requests go out one at a time instead of piling up
                    // concurrently on the same session, and catch so a failure here
                    // doesn't turn into an unhandled promise rejection or block
                    // accepting the suggestion - one agency's cancellation email
                    // failing shouldn't stop the rest from being sent or the accept
                    // from going through.
                    await api[EMAIL_RESOURCE].sendEmail(preppedValues).catch((error) => {
                        console.error('Failed to send cancelation email for request', request.id, error);
                    });
                }
            }

            const { data: suggestion } = await handleSuggestionSubmit(values);

            /* create deployment */
            const { data: deployment } = await handleDeploymentSubmit({
                careNeedId: nurseRequest.careNeedIds[0],
                customerId: nurseRequest.customerId,
                customerContractId: values.customerContractId,
                agencyId: nurseRequest.agencyId,
                type: 'nurse',
                nurseId: suggestion.nurseId,
                nurseSuggestionId: suggestion.id,
                careFrom: values.careFrom,
                careTill: values.careTill,
                notes: suggestion.notes,
                // arrivalAt: null,
                // departureAt: null,
                withoutArrTransfer: false,
                withoutDepTransfer: false,
                // a1From: null,
                // a1Till: null,
                billingFrom: values.careFrom,
                // billingFromHalf: false,
                billingTill: values.careTill,
                // billingTillHalf: false,
                priceAgency: suggestion.priceAgency,
                // priceAgencyDaily: false,
                surchargeAgency: suggestion.surchargeAgency,
                // a1Notes: '',
                provisionPZH: suggestion.provisionPZH,
                surchargePZH: suggestion.surchargePZH,
                provisionLocation: suggestion.provisionLocation,
                provisionMultiplier: '', // FIXME: missing in form
                systemFee,
            });

            /* close all open nurse requests - only once enough nurses are
               confirmed for this care need, otherwise leave them open so the
               remaining agencies can still be heard from */
            if (enoughNursesConfirmed) {
                await Promise.all(
                    openNurseRequests.map((nurseRequest) =>
                        dispatch(
                            updateNurseRequest({
                                id: nurseRequest.id,
                                closedAt: nurseRequest.closedAt || new Date(),
                            })
                        )
                    )
                );
            }
            const result = { suggestion, deployment };

            if (onClose) {
                return onClose(result);
            }
            return result;
        },
        [
            canAcceptSuggestion,
            acceptBlockedReason,
            otherNurseSuggestionsForCustomer,
            otherConfirmedSuggestionsCount,
            nursesNeeded,
            t,
            handleDeploymentSubmit,
            handleSuggestionSubmit,
            onClose,
            nurseRequest,
            openNurseRequests,
            dispatch,
            agencies,
            api,
            employees,
            userStandardEmailAddress,
            kundenbetreuungEmailAddress,
            systemFee,
        ]
    );

    if (!initialized) {
        // show a loading spinner or placeholder while data is being loaded
        return <LoadingBox loading={loading} />;
    }
    return (
        <LoadingBox loading={loading} initialized={initialized}>
            <Form
                initialValues={initialValues}
                validationSchema={acceptNurseSuggSchema}
                onSubmit={handleSubmit}
                onChange={handleOnChange}
                subject={nurseSuggestion || NURSE_SUGGESTION_RESOURCE}
            >
                <DefaultAgencyContractSelector contracts={contracts} agencyId={nurseRequest?.agencyId} />
                {acceptBlockedReason && (
                    <Alert severity="warning" style={{ marginBottom: 16 }}>
                        {acceptBlockedReason}
                    </Alert>
                )}
                {nursesNeeded > 1 && (
                    <Alert
                        severity={
                            // eslint-disable-next-line no-nested-ternary
                            totalConfirmedIncludingThis > nursesNeeded
                                ? 'warning'
                                : totalConfirmedIncludingThis >= nursesNeeded
                                ? 'success'
                                : 'info'
                        }
                        style={{ marginBottom: 16 }}
                    >
                        {nurseSuggestion?.confirmedAt ? (
                            <>
                                Es sind bereits {totalConfirmedIncludingThis} von {nursesNeeded} benötigten
                                Betreuungskräften bestätigt.{' '}
                            </>
                        ) : (
                            <>
                                Mit dieser Bestätigung wären {totalConfirmedIncludingThis} von {nursesNeeded}{' '}
                                benötigten Betreuungskräften bestätigt.{' '}
                                {totalConfirmedIncludingThis >= nursesNeeded
                                    ? 'Die restlichen BK-Anfragen werden nun abgesagt.'
                                    : 'Die übrigen Agenturen werden daher noch nicht abgesagt.'}{' '}
                            </>
                        )}
                        {isOverbooked && (
                            <>
                                Das sind mehr, als aktuell benötigt werden - z.B. weil die Anzahl benötigter
                                Betreuungskräfte nachträglich verringert wurde, nachdem bereits mehr bestätigt waren.
                                Bitte prüfen, ob eine der Bestätigungen zurückgenommen oder die Anzahl wieder erhöht
                                werden sollte.{' '}
                            </>
                        )}
                        {otherConfirmedSuggestions.length > 0 && (
                            <>
                                Bereits bestätigt:{' '}
                                {otherConfirmedSuggestions.map((suggestion, index) => (
                                    <React.Fragment key={suggestion.id}>
                                        {index > 0 && ', '}
                                        <ConfirmedSuggestionSummary
                                            suggestion={suggestion}
                                            nurseRequests={nurseRequests}
                                            agencies={agencies}
                                        />
                                    </React.Fragment>
                                ))}
                                .{' '}
                            </>
                        )}
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <Link
                            href="#"
                            onClick={(event) => {
                                event.preventDefault();
                                handleEditCareNeed(nurseRequest.careNeedIds[0]);
                            }}
                        >
                            Anzahl benötigter Betreuungskräfte ändern
                        </Link>
                    </Alert>
                )}
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <DateInput name="careFrom" label="Betreuung geplant von" fullWidth />
                    </Grid>

                    <Grid item xs={6}>
                        <DateInput name="careTill" label="Betreuung geplant bis" fullWidth />
                    </Grid>
                    <Grid item xs={6}>
                        <DateInput name="confirmedAt" label="Kunde bestätigt" fullWidth />
                    </Grid>
                    <Grid item xs={12}>
                        <ContractSelect
                            label="Vertrag"
                            name="customerContractId"
                            customerId={nurseRequest && nurseRequest.customerId}
                            filter={(contract) =>
                                nurseRequest && contract.agencyId === nurseRequest.agencyId
                            }
                            fullWidth
                        />
                    </Grid>
                </Grid>
                <SubmitButton container={submitRef} disabled={!canAcceptSuggestion} />
            </Form>
        </LoadingBox>
    );
};

AcceptNurseSuggestionForm.propTypes = {
    onClose: PropTypes.func.isRequired,
    nurseSuggestionId: IdPropType.isRequired,
    submitRef: RefPropType,
};

AcceptNurseSuggestionForm.defaultProps = {
    submitRef: null,
};

export default AcceptNurseSuggestionForm;
